import Avatar from 'ext-ui/components/Avatar/Avatar'
import React, { ReactElement } from 'react'
import SvgPencilIcon from 'src/components/Svgs/SvgPencilIcon'
import { ExtendCurrency } from 'src/enums'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'

import { PhysicalCardManagementStatus } from '../cardDetailHeroDemoData'
import { initialsFrom, toDisplayAmount } from '../cardDetailHeroFormat'
import StatusPill from '../StatusPill/StatusPill'
import { useCardDetailHeroData } from '../useCardDetailHeroData'
import * as style from './CardSummary.style'

// The currency-aware `toDisplayAmount` (review finding #1) lives in
// cardDetailHeroFormat, imported above, so both hero variants share it.

type CardSummaryProps = {
  /**
   * Card status is owned by the hero shell — so the pill, the card-art overlay,
   * and the freeze button never diverge — and passed in, rather than read from
   * the demo data here.
   */
  status: PhysicalCardManagementStatus
}

const CardSummary: React.FC<CardSummaryProps> = ({ status }): ReactElement => {
  const translate = useTranslate()
  const currencyFormatter = useCurrencyFormatter()
  const { card, limits } = useCardDetailHeroData()

  const formattedAvailable = currencyFormatter({
    amount: toDisplayAmount(limits.cardFinancials.availableSpend),
    currency: limits.cardFinancials.availableSpend.currency as ExtendCurrency,
    maximumFractionDigits: 0,
  })
  const formattedLimit = currencyFormatter({
    amount: toDisplayAmount(limits.permanentCardSpendControl.limit),
    currency: limits.permanentCardSpendControl.limit.currency as ExtendCurrency,
    maximumFractionDigits: 0,
  })

  return (
    <div css={style.wrapper}>
      <div>
        <div css={style.identityRow}>
          <Avatar
            size={28}
            fallback={
              <span className="fallback-text">
                {initialsFrom(card.cardholder.displayName)}
              </span>
            }
          />
          <h2 css={style.cardholderName} data-testid="cardholder-name">
            {card.cardholder.displayName}
          </h2>
        </div>
        <div css={style.metaRow}>
          <span css={style.metaText}>
            <button
              type="button"
              // WCAG 2.5.3 (Label in Name): the accessible name must contain the
              // visible label, or speech-input users cannot target the control by
              // the only text they can see.
              aria-label={translate('${nickname}, edit card nickname', {
                nickname: card.nickname,
              })}
              css={style.nicknameTrigger}
            >
              {card.nickname}
              <SvgPencilIcon
                width={20}
                height={20}
                fill="currentColor"
                stroke="currentColor"
              />
            </button>
            {' · '}
            {translate('Physical')}
            {` · •• ${card.last4}`}
          </span>
          <StatusPill status={status} />
        </div>
      </div>
      <div css={style.creditBlock}>
        <span css={style.creditLabel}>{translate('Available credit')}</span>
        <span css={style.creditAmount}>{formattedAvailable}</span>
        <span css={style.creditLimit}>
          {translate('of ${limit} limit', { limit: formattedLimit })}
        </span>
      </div>
    </div>
  )
}

export default CardSummary
